import { isApiError, type ApiClient } from '../api/client';
import { streamRunEvents } from '../api/runEvents';
import type { Run, RunEvent } from '../api/types';
import { eventLogReducer, initialEventLog, type EventLogState } from '../lib/eventLog';

export type StreamState = 'idle' | 'loading' | 'live' | 'reconnecting' | 'closed' | 'failed';

export interface RunLogSnapshot {
  log: EventLogState;
  stream: StreamState;
  error: unknown;
  /** The run from `run-completed`, once it arrived. */
  completed: Run | null;
}

const PAGE = 1000;
/** A stream with no subscribers is closed this long after the last one left (spec 6.8.12). */
export const CLOSE_DELAY_MS = 2000;

const EMPTY: RunLogSnapshot = { log: initialEventLog, stream: 'idle', error: null, completed: null };

interface Entry {
  snapshot: RunLogSnapshot;
  listeners: Set<() => void>;
  completedHandlers: Set<(run: Run) => void>;
  controller: AbortController;
  closeTimer: ReturnType<typeof setTimeout> | null;
}

/**
 * One event subscription per run (spec 6.8.12): every view of a run's events shares the entry, which holds
 * the merged log. The first subscriber starts it (JSON pages for a terminal run, else the SSE stream); the
 * stream is closed `CLOSE_DELAY_MS` after the last subscriber left, unless one returns.
 */
export class RunEventStreams {
  private readonly entries = new Map<string, Entry>();
  private readonly api: ApiClient;
  private readonly closeDelayMs: number;

  constructor(api: ApiClient, closeDelayMs = CLOSE_DELAY_MS) {
    this.api = api;
    this.closeDelayMs = closeDelayMs;
  }

  /** Number of runs with an open entry (tests). */
  get size(): number {
    return this.entries.size;
  }

  snapshot(runId: string): RunLogSnapshot {
    return this.entries.get(runId)?.snapshot ?? EMPTY;
  }

  /**
   * Subscribes to a run's events. `terminal` says whether the run is finished (then the JSON form is loaded)
   * when the entry is created. `onCompleted` gets `run-completed`, also when it arrived before.
   */
  subscribe(
    runId: string,
    terminal: boolean,
    listener: () => void,
    onCompleted?: (run: Run) => void,
  ): () => void {
    let entry = this.entries.get(runId);
    if (!entry) {
      entry = {
        snapshot: { ...EMPTY },
        listeners: new Set(),
        completedHandlers: new Set(),
        controller: new AbortController(),
        closeTimer: null,
      };
      this.entries.set(runId, entry);
      this.start(runId, entry, terminal);
    }
    if (entry.closeTimer) {
      clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    }
    const current = entry;
    current.listeners.add(listener);
    const handler = onCompleted ? (run: Run) => onCompleted(run) : null;
    if (handler) {
      current.completedHandlers.add(handler);
      const completed = current.snapshot.completed;
      if (completed) queueMicrotask(() => current.completedHandlers.has(handler) && handler(completed));
    }
    return () => {
      current.listeners.delete(listener);
      if (handler) current.completedHandlers.delete(handler);
      if (current.listeners.size > 0 || current.closeTimer) return;
      current.closeTimer = setTimeout(() => {
        if (current.listeners.size > 0 || this.entries.get(runId) !== current) return;
        current.controller.abort();
        this.entries.delete(runId);
      }, this.closeDelayMs);
    };
  }

  private update(entry: Entry, patch: Partial<RunLogSnapshot>) {
    entry.snapshot = { ...entry.snapshot, ...patch };
    for (const listener of [...entry.listeners]) listener();
  }

  private start(runId: string, entry: Entry, terminal: boolean) {
    const { signal } = entry.controller;
    const received = (events: readonly RunEvent[]) => {
      if (signal.aborted) return;
      const log = eventLogReducer(entry.snapshot.log, { type: 'received', events });
      if (log !== entry.snapshot.log) this.update(entry, { log });
    };

    const loadAll = async () => {
      this.update(entry, { stream: 'loading' });
      let after = 0;
      for (;;) {
        const page = await this.api.getRunEvents(runId, after, PAGE);
        if (signal.aborted) return;
        received(page.items);
        if (page.items.length < PAGE || page.nextAfter === after) break;
        after = page.nextAfter;
      }
      this.update(entry, { stream: 'closed' });
    };

    const follow = async () => {
      this.update(entry, { stream: 'live' });
      await streamRunEvents(
        this.api,
        runId,
        {
          onEvent: (event) => {
            if (entry.snapshot.stream !== 'live') this.update(entry, { stream: 'live' });
            received([event]);
          },
          onCompleted: (run) => {
            this.update(entry, { stream: 'closed', completed: run });
            for (const handler of [...entry.completedHandlers]) handler(run);
          },
          onReconnecting: () => this.update(entry, { stream: 'reconnecting' }),
        },
        { signal },
      );
    };

    (terminal ? loadAll() : follow()).catch((error: unknown) => {
      if (signal.aborted) return;
      this.update(entry, { stream: 'failed', error });
      if (!isApiError(error)) console.error(error);
    });
  }
}

const registries = new WeakMap<ApiClient, RunEventStreams>();

/** The shared subscriptions of one API client (one per browser tab). */
export function runEventStreams(api: ApiClient): RunEventStreams {
  let streams = registries.get(api);
  if (!streams) {
    streams = new RunEventStreams(api);
    registries.set(api, streams);
  }
  return streams;
}
